import { ContactDropdownTypeEnum } from "ui/components/Dropdown/ContactDropdown/types";

import { FleetVehicleStatusEnum } from "../types";
import {
  VehicleLocationFormValues,
  VehicleLocationTypeConfig,
  VehicleLocationTypeEnum,
} from "./types";

/**
 * Što svaka vrsta lokacije traži i kako se zove u sučelju.
 * Skladišta nisu vrsta lokacije vozila — ona su vezana uz gume.
 */
export const VEHICLE_LOCATION_TYPE_CONFIG: Record<
  VehicleLocationTypeEnum,
  VehicleLocationTypeConfig
> = {
  [VehicleLocationTypeEnum.OFFICE]: {
    label: "Poslovnica",
    holderLabel: "Poslovnica",
    icon: "/media/icons/company.svg",
    description: "Vozilo je kod nas, u jednoj od poslovnica",
  },
  [VehicleLocationTypeEnum.SUPPLIER]: {
    label: "Kod dobavljača",
    holderLabel: "Dobavljač",
    contactGroupIds: ["S"],
    // Dobavljač je najčešće tvrtka, ali zna biti i fizička osoba — zato prijedlog,
    // ne ograničenje.
    defaultContactType: ContactDropdownTypeEnum.COMPANY,
    icon: "/media/icons/contact.svg",
    description: "Vozilo je kupljeno, ali još nije preuzeto",
  },
  [VehicleLocationTypeEnum.CLIENT]: {
    label: "Kod klijenta",
    holderLabel: "Klijent",
    contactGroupIds: ["B"],
    icon: "/media/icons/users.svg",
    description: "Vozilo je kod klijenta, npr. u najmu",
  },
  // Svaka servisna vrsta nudi svoju grupu; bez toga bi sve tri vukle isti popis dobavljača.
  [VehicleLocationTypeEnum.SERVICE]: {
    label: "Na servisu",
    holderLabel: "Servis",
    contactGroupIds: ["A"],
    contactType: ContactDropdownTypeEnum.COMPANY,
    icon: "/media/icons/service.svg",
    description: "Vozilo je zaprimljeno na servis",
  },
  [VehicleLocationTypeEnum.DAMAGE_REPAIR]: {
    label: "Na popravku štete",
    holderLabel: "Limar",
    contactGroupIds: ["L"],
    contactType: ContactDropdownTypeEnum.COMPANY,
    icon: "/media/icons/damage.svg",
    description: "Vozilo je na popravku štete",
  },
  [VehicleLocationTypeEnum.TIRE_CHANGE]: {
    label: "Na promjeni guma",
    holderLabel: "Vulkanizer",
    contactGroupIds: ["V"],
    contactType: ContactDropdownTypeEnum.COMPANY,
    icon: "/media/icons/tire.svg",
    description: "Vozilo je na zamjeni garniture guma",
  },
  [VehicleLocationTypeEnum.OTHER]: {
    label: "Ostalo",
    holderLabel: "Adresa",
    icon: "/media/icons/map.svg",
    description: "Adresa se upisuje ručno",
  },
};

/** Prijedlozi i veznici u nazivu vrste — ne nose značenje, pa ne ulaze u inicijale. */
const LOCATION_LABEL_FILLER_WORDS = ["na", "kod", "u", "i"];

/**
 * Inicijali vrste lokacije za ikonu u tablici: "Na popravku štete" → "PŠ".
 * Prijedlozi se preskaču jer bi inače "Na popravku štete" i "Na promjeni guma" dale isto.
 */
export const getVehicleLocationTypeInitials = (
  locationType: VehicleLocationTypeEnum | null
): string => {
  const label = locationType ? VEHICLE_LOCATION_TYPE_CONFIG[locationType]?.label : null;

  if (!label) return "?";

  return label
    .split(" ")
    .filter((word) => !LOCATION_LABEL_FILLER_WORDS.includes(word.toLowerCase()))
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
};

export const ALL_VEHICLE_LOCATION_TYPES: VehicleLocationTypeEnum[] = [
  VehicleLocationTypeEnum.OFFICE,
  VehicleLocationTypeEnum.SUPPLIER,
  VehicleLocationTypeEnum.CLIENT,
  VehicleLocationTypeEnum.SERVICE,
  VehicleLocationTypeEnum.DAMAGE_REPAIR,
  VehicleLocationTypeEnum.TIRE_CHANGE,
  VehicleLocationTypeEnum.OTHER,
];

/** Vrste kod kojih je nositelj lokacije kontakt. */
export const locationRequiresContact = (
  locationType: VehicleLocationTypeEnum | null
): boolean =>
  locationType === VehicleLocationTypeEnum.SUPPLIER ||
  locationType === VehicleLocationTypeEnum.CLIENT ||
  locationType === VehicleLocationTypeEnum.SERVICE ||
  locationType === VehicleLocationTypeEnum.DAMAGE_REPAIR ||
  locationType === VehicleLocationTypeEnum.TIRE_CHANGE;

export const locationRequiresOffice = (
  locationType: VehicleLocationTypeEnum | null
): boolean => locationType === VehicleLocationTypeEnum.OFFICE;

export const locationRequiresAddress = (
  locationType: VehicleLocationTypeEnum | null
): boolean => locationType === VehicleLocationTypeEnum.OTHER;

/**
 * Ima li lokacija nositelja koji njezina vrsta traži. Nepotpuna lokacija se ne šalje —
 * backend je odbija (`BuildVehicleLocationAsync`), a čarobnjak sprema svaki korak, pa bi
 * poluispunjen obrazac rušio spremanje ostalih koraka.
 */
export const isVehicleLocationComplete = (
  values: VehicleLocationFormValues | null
): boolean => {
  if (!values || !values.locationType) return false;

  if (locationRequiresOffice(values.locationType)) return !!values.officeId;
  if (locationRequiresContact(values.locationType)) return !!values.contactId;
  if (locationRequiresAddress(values.locationType))
    return !!values.addressLine1 || !!values.addressCity;

  return true;
};

/**
 * Status koji vrsta lokacije podrazumijeva. Vozilo kod limara nije slobodno, a vozilo
 * vraćeno u poslovnicu opet jest — premještaj to nudi umjesto da korisnik ide na značku.
 *
 * Promjena guma i odlazak klijentu nemaju prijedlog: gume se mijenjaju u sat vremena, a
 * najam status dobiva iz ugovora.
 */
const LOCATION_TYPE_SUGGESTED_STATUS: Partial<
  Record<VehicleLocationTypeEnum, FleetVehicleStatusEnum>
> = {
  [VehicleLocationTypeEnum.SERVICE]: FleetVehicleStatusEnum.UNAVAILABLE_SERVICE,
  [VehicleLocationTypeEnum.DAMAGE_REPAIR]: FleetVehicleStatusEnum.UNAVAILABLE_DAMAGE,
  [VehicleLocationTypeEnum.SUPPLIER]: FleetVehicleStatusEnum.ARRIVING,
  [VehicleLocationTypeEnum.OFFICE]: FleetVehicleStatusEnum.AVAILABLE,
};

/** Statusi u kojima vozilo stoji zbog zahvata — iz njih ga povratak u poslovnicu oslobađa. */
const UNAVAILABLE_STATUSES: FleetVehicleStatusEnum[] = [
  FleetVehicleStatusEnum.UNAVAILABLE,
  FleetVehicleStatusEnum.UNAVAILABLE_SERVICE,
  FleetVehicleStatusEnum.UNAVAILABLE_DAMAGE,
  FleetVehicleStatusEnum.UNAVAILABLE_DOCUMENTATION,
];

/**
 * Status koji se nudi uz premještaj, ili `null` kad se ništa ne nudi.
 *
 * Prijedlog otpada kad je vozilo već u tom statusu i kad bi povratak u poslovnicu
 * "oslobodio" vozilo koje nije bilo nedostupno — prodano ili rezervirano vozilo ne
 * postaje slobodno time što je stiglo k nama.
 */
export const getSuggestedStatus = (
  locationType: VehicleLocationTypeEnum | null,
  currentStatus: FleetVehicleStatusEnum | null
): FleetVehicleStatusEnum | null => {
  if (!locationType) return null;

  const suggested = LOCATION_TYPE_SUGGESTED_STATUS[locationType] ?? null;

  if (!suggested || suggested === currentStatus) return null;

  if (
    suggested === FleetVehicleStatusEnum.AVAILABLE &&
    !UNAVAILABLE_STATUSES.includes(currentStatus as FleetVehicleStatusEnum)
  ) {
    return null;
  }

  return suggested;
};

export const initialVehicleLocationForm: VehicleLocationFormValues = {
  locationType: VehicleLocationTypeEnum.OFFICE,
  name: null,
  officeId: null,
  contactId: null,
  addressLine1: null,
  addressCity: null,
  addressPostalCode: null,
  addressCountry: null,
  note: null,
  driverId: null,
  validFrom: null,
};
